// ./src/components/generic/DisplaySelector/DisplaySelector.tsx
import React, { useMemo, useEffect } from "react";
import { Select, MultiSelect, MultiSelectProps } from "@mantine/core";
import { useDisplays } from "@/hooks/useDisplays";
import { LoadingIndicator } from "@/components/generic";
import { Display } from "@/utils";

export interface DisplaySelectorProps {
  /** Selected display ID(s) - string for single select, string[] for multiple */
  value: string | string[] | null;

  /** Callback when display selection changes */
  onChange?: (
    displayId: string | string[] | null,
    display?: Display | Display[]
  ) => void;

  /** Venue ID to filter displays by */
  venueId?: string | undefined;

  /** Layout ID to filter displays by */
  layoutId?: string;

  /** Whether to allow multiple selection */
  multiple?: boolean;

  /** Placeholder text */
  placeholder?: string;

  /** Whether to include inactive displays */
  includeInactive?: boolean;

  /** Custom filter function for displays */
  displayFilter?: (display: Display) => boolean;

  /** Whether the field can be cleared */
  clearable?: boolean;

  /** Whether the field is required */
  required?: boolean;

  /** Loading placeholder text */
  loadingPlaceholder?: string;

  /** Empty state text */
  emptyText?: string;

  /** Error message */
  error?: React.ReactNode;

  /** Whether the field is disabled */
  disabled?: boolean;

  /** Component size */
  size?: "xs" | "sm" | "md" | "lg" | "xl";

  /** Additional props passed to Select/MultiSelect */
  [key: string]: any;
}

export const DisplaySelector: React.FC<DisplaySelectorProps> = ({
  value,
  onChange,
  venueId,
  layoutId,
  multiple = false,
  placeholder = "Select a display",
  includeInactive = false,
  displayFilter,
  clearable = true,
  required = false,
  loadingPlaceholder = "Loading displays...",
  emptyText = "No displays available",
  error,
  disabled,
  size = "sm",
  ...selectProps
}) => {
  // Use displays hook with venue/layout filter
  const { displays, loading, isEmpty, lastError } = useDisplays({
    venueId,
    layoutId,
    autoLoad: true,
  });

  // Filter and prepare display options
  const displayOptions = useMemo(() => {
    let filteredDisplays = displays;

    // Apply status filter (status 100 = active, 0 = disabled)
    if (!includeInactive) {
      filteredDisplays = filteredDisplays.filter(
        (display) => display.status === 100 // Only show active displays
      );
    }

    // Apply custom filter if provided
    if (displayFilter) {
      filteredDisplays = filteredDisplays.filter(displayFilter);
    }

    // Sort by name for better UX
    filteredDisplays.sort((a, b) => {
      const nameA = a.name || "Unnamed Display";
      const nameB = b.name || "Unnamed Display";
      return nameA.localeCompare(nameB);
    });

    // Convert to select options
    return filteredDisplays.map((display) => ({
      value: display.id || "",
      label: display.name || "Unnamed Display",
      disabled: display.status === 0, // Disabled displays have status 0
    }));
  }, [displays, includeInactive, displayFilter]);

  // Create a map for quick display lookup by ID
  const displayMap = useMemo(() => {
    const map = new Map();
    displays.forEach((display) => {
      if (display.id) {
        map.set(display.id, display);
      }
    });
    return map;
  }, [displays]);

  // Auto-select first display if required and no value provided
  useEffect(() => {
    if (required && displayOptions.length > 0 && !loading && onChange) {
      // For single select mode
      if (!multiple && !value) {
        const firstDisplay = displayOptions[0];
        if (firstDisplay && !firstDisplay.disabled) {
          const displayObject = displayMap.get(firstDisplay.value);
          onChange(firstDisplay.value, displayObject);
        }
      }
      // For multiple select mode
      else if (
        multiple &&
        (!value || (Array.isArray(value) && value.length === 0))
      ) {
        const firstDisplay = displayOptions[0];
        if (firstDisplay && !firstDisplay.disabled) {
          const displayObject = displayMap.get(firstDisplay.value);
          onChange([firstDisplay.value], [displayObject]);
        }
      }
    }
  }, [
    required,
    value,
    displayOptions,
    loading,
    onChange,
    displayMap,
    multiple,
  ]);

  // Handle onChange with display object lookup
  const handleChange = (selectedValue: string | string[] | null) => {
    if (!multiple) {
      // Single select mode
      const selectedDisplayId = selectedValue as string | null;
      if (selectedDisplayId) {
        const selectedDisplay = displayMap.get(selectedDisplayId);
        onChange?.(selectedDisplayId, selectedDisplay);
      } else {
        // When clearing selection, pass null for both ID and display
        onChange?.(null, undefined);
      }
    } else {
      // Multiple select mode
      const selectedDisplayIds = selectedValue as string[] | null;
      if (selectedDisplayIds && selectedDisplayIds.length > 0) {
        const selectedDisplays = selectedDisplayIds
          .map((id) => displayMap.get(id))
          .filter(Boolean); // Remove any undefined values
        onChange?.(selectedDisplayIds, selectedDisplays);
      } else {
        // When clearing selection, pass empty arrays
        onChange?.([], []);
      }
    }
  };

  // Determine the current state and configure select props accordingly
  const getSelectState = () => {
    // Loading state
    if (loading && displays.length === 0) {
      return {
        data: [],
        placeholder: loadingPlaceholder,
        disabled: true,
        rightSection: <LoadingIndicator size={size} color="gray" />,
      };
    }

    // Error state
    if (lastError && !loading) {
      return {
        data: [],
        placeholder: "Error loading displays",
        disabled: true,
        error: error || "Failed to load displays",
      };
    }

    // Empty state
    if (isEmpty && !loading) {
      return {
        data: [],
        placeholder: emptyText,
        disabled: true,
      };
    }

    // Normal state with data
    return {
      data: displayOptions,
      placeholder,
      disabled: disabled || loading,
      rightSection:
        loading && displays.length > 0 ? (
          <LoadingIndicator size={size} color="gray" />
        ) : (
          selectProps.rightSection
        ),
    };
  };

  const selectState = getSelectState();

  // Normalize value for the select component
  const normalizedValue = multiple
    ? Array.isArray(value)
      ? value
      : []
    : value ?? null;

  const renderMultiSelectOption: MultiSelectProps["renderOption"] = ({
    option,
  }) => {
    return option.label;
  };

  // Render the appropriate select component
  if (multiple) {
    return (
      <MultiSelect
        {...selectProps}
        size={size}
        data={selectState.data}
        renderOption={renderMultiSelectOption}
        value={normalizedValue as string[]}
        onChange={handleChange}
        placeholder={selectState.placeholder}
        clearable={clearable}
        searchable
        required={required}
        disabled={selectState.disabled}
        error={selectState.error || error}
        rightSection={selectState.rightSection}
      />
    );
  }

  // Render single select
  return (
    <Select
      {...selectProps}
      size={size}
      data={selectState.data}
      value={normalizedValue as string | null}
      onChange={handleChange}
      placeholder={selectState.placeholder}
      clearable={!required && clearable}
      searchable
      required={required}
      disabled={selectState.disabled}
      error={selectState.error || error}
      rightSection={selectState.rightSection}
    />
  );
};
